import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { FBAuth } from '@/helpers/firebaseServices';
import {
  APIPasskeyDelete,
  APIPasskeyList,
  APIPasskeyLoginBegin,
  APIPasskeyLoginFinish,
  APIPasskeyRegisterBegin,
  APIPasskeyRegisterFinish,
  APIPasskeyRename,
} from '@/api';

/**
 * Passkey sign-in and enrolment.
 *
 * The private key never reaches us. The browser hands it to the platform credential manager —
 * iCloud Keychain on a Mac or iPhone, Google Password Manager on Chrome and Android — which is
 * also what syncs it to the customer's other devices; there is nothing for us to synchronise.
 * We hold the public half against the relying party eventor.app, which is the same relying
 * party the iOS and Android apps use, so a passkey enrolled on a phone signs in here too.
 */

/** Whether this browser can do WebAuthn at all. Safari 16+, Chrome, Edge, Firefox. */
export function passkeysAreSupported() {
  return browserSupportsWebAuthn();
}

/**
 * Whether this browser can offer the passkey inside its own autofill list (conditional UI).
 * Async because it asks the platform, not just the API surface.
 */
export function passkeyAutofillIsSupported() {
  if (!browserSupportsWebAuthn()) return Promise.resolve(false);
  return browserSupportsWebAuthnAutofill().catch(() => false);
}

/**
 * A name for the list in account settings. The browser will not tell us which device this is,
 * so this is a guess from the user agent, and the customer can rename it afterwards.
 */
function guessDeviceLabel() {
  const ua = navigator.userAgent || '';
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || '';

  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android device';
  if (/Mac OS X|macOS/.test(ua) || platform === 'macOS') return 'Mac';
  if (/Windows/.test(ua) || platform === 'Windows') return 'Windows PC';
  if (/CrOS/.test(ua)) return 'Chromebook';
  return 'This device';
}

/** WebAuthn errors read like spec text; these are the ones a customer can actually hit. */
function readableError(error) {
  if (!error) return new Error('The passkey could not be used.');
  if (error.name === 'NotAllowedError') return new Error('The passkey prompt was cancelled or timed out.');
  if (error.name === 'InvalidStateError') return new Error('This device already has a passkey for your Eventor account.');
  if (error.name === 'SecurityError') return new Error('Passkeys are not available on this address.');
  if (error.response && error.response.data && error.response.data.message) {
    return new Error(error.response.data.message);
  }
  return new Error(error.message || 'The passkey could not be used.');
}

/** Enrols a passkey on the account that is already signed in. */
export async function enrolPasskey(label) {
  try {
    const { data: begin } = await APIPasskeyRegisterBegin();
    const response = await startRegistration({ optionsJSON: begin.options });

    const { data } = await APIPasskeyRegisterFinish({
      challengeId: begin.challengeId,
      response,
      label: label || guessDeviceLabel(),
    });

    return data;
  } catch (error) {
    throw readableError(error);
  }
}

/**
 * Signs in with a passkey.
 *
 * `email` is optional: without it the ceremony is usernameless and the authenticator offers
 * whatever Eventor passkeys it holds. `useAutofill` is the conditional-UI variant, started
 * quietly when the sign-in form mounts so the passkey shows up in the browser's own autofill
 * list on the e-mail field instead of behind a button.
 *
 * Nothing is committed to the store here: plugins/firebaseAuth.js listens on
 * onAuthStateChanged and fills the store (and LogRocket) for every sign-in method.
 */
export async function signInWithPasskey({ email, useAutofill } = {}) {
  try {
    const { data: begin } = await APIPasskeyLoginBegin({ email: email || null });
    const response = await startAuthentication({
      optionsJSON: begin.options,
      useBrowserAutofill: Boolean(useAutofill),
    });

    const { data: finish } = await APIPasskeyLoginFinish({
      challengeId: begin.challengeId,
      response,
    });

    const result = await FBAuth.signInWithCustomToken(finish.customToken);

    const { dataLayer } = window;
    if (dataLayer) dataLayer.push({ event: 'user-signed-in-with-passkey' });

    return result.user;
  } catch (error) {
    throw readableError(error);
  }
}

export async function listPasskeys() {
  const { data } = await APIPasskeyList();
  return data.passkeys || [];
}

export async function removePasskey(credentialId) {
  await APIPasskeyDelete({ credentialId });
}

export async function renamePasskey(credentialId, label) {
  await APIPasskeyRename({ credentialId, label });
}
